import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { FeatureUserStore } from '@h5s/feature-user';
import { AuthService, UtilAuth } from '@h5s/util-auth';
import { BackendErrorCode, isSimpleBackendErrorWithCode } from '@h5s/util-error';
import { LoggerService } from '@h5s/util-logger';
import { OAuthModuleConfig, OAuthService } from 'angular-oauth2-oidc';
import { catchError, EMPTY, from, Observable, switchMap, throwError } from 'rxjs';

/**
 * Interceptor per gestire l'autenticazione delle richieste HTTP
 * Se l'URL della richiesta è tra quelli configurati, aggiunge l'access_token nell'header 'Authorization'
 *
 * @param req La richiesta HTTP
 * @param next Il gestore della richiesta successivo
 * @returns La risposta HTTP
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggerService).getLogger('AuthInterceptor');
  const oauthService = inject(OAuthService);
  const authService = inject(AuthService);
  const config = inject(OAuthModuleConfig);
  const featureUserStore = inject(FeatureUserStore);

  const sendAccessToken = config?.resourceServer?.sendAccessToken;
  const allowedUrls = config?.resourceServer?.allowedUrls || [];

  const url = req.url.toLowerCase();
  const isUrlAllowed = allowedUrls.some((u) => url.startsWith(u));

  /** Controlla se l'URL è tra quelli permessi e se deve inviare il token di accesso */
  if (isUrlAllowed && sendAccessToken) {
    req = UtilAuth.addAuthorizationHeader(req, oauthService.getAccessToken());
    req = UtilAuth.addIdTokenHeader(req, oauthService.getIdToken());
  }

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || !isUrlAllowed) {
        return throwError(() => err);
      }

      // Estraiamo l'errore dalla HTTP Response (sia JSON che Blob)
      const parseError = (e: HttpErrorResponse): Observable<unknown> => {
        if (e.error instanceof Blob) {
          return from(e.error.text()).pipe(switchMap((obj) => [JSON.parse(obj)]));
        }
        return from([e.error]);
      };

      return parseError(err).pipe(
        switchMap((parsedError) => {
          // Se ExpiredAccessTokenException, gestiamo il refreshToken e ritentiamo la richiesta
          if (isSimpleBackendErrorWithCode(parsedError, BackendErrorCode.ExpiredAccessTokenException)) {
            return UtilAuth.handle401WithRefreshToken(req, next, oauthService, authService, logger);
          }

          // Se InvalidAccessTokenException, reindirizziamo al login e resettiamo lo store
          if (isSimpleBackendErrorWithCode(parsedError, BackendErrorCode.InvalidAccessTokenException)) {
            featureUserStore.resetUserAccessProperty();
            authService.revokeTokenAndLogout();

            // Logout/redirect alla login in corso: completiamo in silenzio per non mostrare
            // un toast di errore tramite il GlobalErrorHandler.
            return EMPTY;
          }

          return throwError(() => err);
        }),
      );
    }),
  );
};
